// ./src/components/features/VolunteerSlots/VolunteerSlotEditDrawer/VolunteerSlotEditDrawer.tsx
//
// Create/edit drawer for an event volunteer slot: role, quantity, shift times,
// location (venue / POI / address / coordinates + radius), instructions, and coupon
// rewards, and the pay (#A4.7 — an hourly rate that overrides the role's, in the event's currency). Self-loading in edit
// mode; no success toast (the page highlight confirms).

import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Stack, TextInput, Textarea, NumberInput, Group, Divider, Skeleton, Radio } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";
import dayjs from "dayjs";

import { VolunteerRole, VolunteerSlot, VolunteerSlotInput, VolunteerSlotRewardInput, VOLUNTEER_APPROVAL_MODE } from "@/utils/api";
import { getFormErrors, formatErrors, formatCurrency, utcToEventInput, eventInputToUtc, eventTzAbbrev, latestEnd, moveEndWithStart } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { useOptionalVolunteerSlotsContext } from "@/contexts/VolunteerSlotsContext";
import { useCrewAccess, useCurrentEvent } from "@/contexts";
import { CREW_CAPABILITY } from "@/config/crewCapabilities";
import { AppDrawer, StatusInput, DateTimeField } from "@/components/generic";
import { MoneyInput } from "@/components/features/Budget/MoneyInput/MoneyInput";

import { VolunteerRoleSelect } from "../VolunteerRoleSelect/VolunteerRoleSelect";
import { VolunteerLocationFields, VolunteerLocationValue, EMPTY_VOLUNTEER_LOCATION } from "../VolunteerLocationFields/VolunteerLocationFields";
import { VolunteerRewardEditor } from "../VolunteerRewardEditor/VolunteerRewardEditor";

export interface VolunteerSlotEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  recordId?: string | null;
  /** IANA timezone of the event — shift times are edited in this zone (stored UTC). */
  eventTimezone?: string | null;
  onCreated?: (slot: VolunteerSlot) => void;
  onUpdated?: (slot: VolunteerSlot) => void;
  /**
   * The event's other shifts. A NEW shift starts where the last one ENDS, for as long as that one ran (owner, 2026-09-30:
   * "when user adds several time slots … auto-calculate next start time as end of previous").
   */
  siblings?: VolunteerSlot[];
}

interface FormData {
  name: string;
  volunteerRoleId: string | null;
  quantityNeeded: number;
  startsAt: string | null;
  endsAt: string | null;
  location: VolunteerLocationValue;
  instructions: string;
  rewards: VolunteerSlotRewardInput[];
  status: number;
  /** ⚠ null on a NEW slot — the host must choose; the server refuses a create without it (#A4.6). */
  isOpenForApplications: boolean | null;
  /** ⚠ '' on a new slot, for the same reason. */
  approvalMode: string;
  applicationsCloseAt: string | null;
  /** #A4.7 — null = the role's rate. */
  hourlyRate: number | null;
}

const KNOWN_FORM_FIELDS = [
  "hourlyRate",
  "name",
  "volunteerRoleId",
  "quantityNeeded",
  "startsAt",
  "endsAt",
  "locationKind",
  "locationVenueId",
  "pointOfInterestId",
  "streetAddress",
  "latitude",
  "longitude",
  "radius",
  "radiusUnit",
  "instructions",
  "rewards",
  "status",
  "isOpenForApplications",
  "approvalMode",
  "applicationsCloseAt",
];

/** A new shift right after the LAST one — its end as the start, its length as the length — or the blank form. */
const afterLastShift = (blank: FormData, siblings: readonly VolunteerSlot[], tz?: string | null): FormData => {
  const last = latestEnd(siblings, (s) => s.endsAt);
  const lastShift = last ? siblings.find((s) => s.endsAt === last) : undefined;

  if (!last || !lastShift?.startsAt) {
    return blank;
  }

  const lengthMs = Math.max(5 * 60 * 1000, dayjs(last).diff(dayjs(lastShift.startsAt)));

  return {
    ...blank,
    startsAt: utcToEventInput(last, tz),
    endsAt: utcToEventInput(dayjs(last).add(lengthMs, "millisecond").toISOString(), tz),
  };
};

const getInitialFormData = (): FormData => ({
  name: "",
  volunteerRoleId: null,
  quantityNeeded: 1,
  startsAt: null,
  endsAt: null,
  location: { ...EMPTY_VOLUNTEER_LOCATION },
  instructions: "",
  rewards: [],
  status: STATUSES.ACTIVE,
  isOpenForApplications: null,
  approvalMode: "",
  applicationsCloseAt: null,
  hourlyRate: null,
});

const fromRecord = (record: VolunteerSlot, tz?: string | null): FormData => ({
  name: record.name || "",
  volunteerRoleId: record.volunteerRoleId || null,
  quantityNeeded: record.quantityNeeded ?? 1,
  // Stored UTC → the event-local wall-clock the picker edits.
  startsAt: utcToEventInput(record.startsAt, tz),
  endsAt: utcToEventInput(record.endsAt, tz),
  location: {
    kind: record.locationKind || null,
    venueId: record.locationVenueId || null,
    pointOfInterestId: record.pointOfInterestId || null,
    address: record.streetAddress || "",
    latitude: record.latitude ?? null,
    longitude: record.longitude ?? null,
    radius: record.radius ?? null,
    radiusUnit: (record.radiusUnit as "m" | "ft") || "m",
    label: record.locationLabel || null,
  },
  instructions: record.instructions || "",
  rewards: (record.rewards || []).map((r) => ({ id: r.id, couponTypeId: r.couponTypeId, quantity: r.quantity })),
  status: record.status ?? STATUSES.ACTIVE,
  isOpenForApplications: record.isOpenForApplications ?? null,
  approvalMode: record.approvalMode || "",
  applicationsCloseAt: utcToEventInput(record.applicationsCloseAt, tz),
  hourlyRate: record.hourlyRate ?? null,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const VolunteerSlotEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={90} radius="sm" />
    <Skeleton height={120} radius="sm" />
  </Stack>
);

export const VolunteerSlotEditDrawer: React.FC<VolunteerSlotEditDrawerProps> = ({
  opened,
  onClose,
  eventId,
  recordId,
  eventTimezone,
  onCreated,
  onUpdated,
  siblings = [],
}) => {
  const { t } = useTranslation(["volunteers", "common"]);
  // Read at OPEN, through a ref: a list reload while the drawer is open must never re-seed what the host is typing.
  const siblingsRef = useRef(siblings);

  siblingsRef.current = siblings;
  const tzAbbrev = useMemo(() => eventTzAbbrev(eventTimezone), [eventTimezone]);
  const { currentEvent } = useCurrentEvent();
  const currencyCode = currentEvent?.currencyCode ?? null;
  // Pay is budget: crew without budget.edit neither see nor send it (the server refuses the field from them).
  const crew = useCrewAccess();
  const canEditPay = !crew.isCrewMode || crew.hasCapability(CREW_CAPABILITY.BUDGET_EDIT);
  const [pickedRole, setPickedRole] = useState<VolunteerRole | null>(null);

  const ctx = useOptionalVolunteerSlotsContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  const [record, setRecord] = useState<VolunteerSlot | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as VolunteerSlot);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  // Own submit result first — a context `lastError` alone swallows errors when the caller saves another way.
  const [submitError, setSubmitError] = useState<OpResult | null>(null);
  const effectiveError = loadError || submitError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  // The shift can't end at or before it starts (a person can't finish before they begin).
  const endBeforeStart = useMemo(
    () => !!formData.startsAt && !!formData.endsAt && dayjs(formData.endsAt).valueOf() <= dayjs(formData.startsAt).valueOf(),
    [formData.startsAt, formData.endsAt]
  );

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record, eventTimezone) : afterLastShift(getInitialFormData(), siblingsRef.current, eventTimezone);
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    setSubmitError(null);
    clearErrRef.current?.();
  }, [opened, record, isEditMode, eventTimezone]);

  const setField = useCallback(<K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Moving the start moves the end by the same amount (the shift keeps its length); the host adjusts the end if needed.
  const setStart = useCallback((value: string | null) => {
    setFormData((prev) => ({ ...prev, startsAt: value, endsAt: value ? moveEndWithStart(prev.startsAt, prev.endsAt, value) : prev.endsAt }));
  }, []);

  const handleSubmit = useCallback(async () => {
    // Guard: end must be strictly after start (client-side; the server re-checks).
    if (formData.startsAt && formData.endsAt && dayjs(formData.endsAt).valueOf() <= dayjs(formData.startsAt).valueOf()) {
      return;
    }

    clearErrRef.current?.();
    setLoadError(null);
    setSubmitError(null);

    const loc = formData.location;
    const submitData: VolunteerSlotInput = {
      name: formData.name.trim() || null,
      volunteerRoleId: formData.volunteerRoleId,
      quantityNeeded: formData.quantityNeeded,
      // The picker edits event-local wall-clock; store the absolute UTC instant.
      startsAt: eventInputToUtc(formData.startsAt, eventTimezone),
      endsAt: eventInputToUtc(formData.endsAt, eventTimezone),
      instructions: formData.instructions.trim() || null,
      locationKind: loc.kind,
      locationVenueId: loc.kind === "venue" ? loc.venueId : null,
      pointOfInterestId: loc.kind === "poi" ? loc.pointOfInterestId : null,
      streetAddress: loc.kind === "address" ? loc.address.trim() || null : null,
      latitude: loc.kind === "address" || loc.kind === "coordinates" ? loc.latitude : null,
      longitude: loc.kind === "address" || loc.kind === "coordinates" ? loc.longitude : null,
      radius: loc.kind === "coordinates" ? loc.radius : null,
      radiusUnit: loc.kind === "coordinates" ? loc.radiusUnit : null,
      // Denormalized label so the list/print show the venue/POI/place name (coords render raw).
      locationLabel: loc.kind && loc.kind !== "coordinates" ? loc.label || null : null,
      rewards: formData.rewards.filter((r) => !!r.couponTypeId),
      status: formData.status,
      // ⚠ Sent as chosen — null / '' on a new slot the host left untouched, so the server's refusal lands on
      // these fields rather than a default quietly deciding who may sign up (#A4.6).
      isOpenForApplications: formData.isOpenForApplications,
      approvalMode: formData.approvalMode || null,
      // ⚠ KEEP THE CLOSING DATE when sign-ups are switched off (found 2026-09-19). This used to send null, so
      // pausing sign-ups quietly deleted the date and switching back lost it, with no warning at any point.
      // Closing the door is not the same as forgetting when it was going to close.
      applicationsCloseAt: eventInputToUtc(formData.applicationsCloseAt, eventTimezone),
    };

    if (canEditPay) {
      submitData.hourlyRate = formData.hourlyRate;
    }

    if (!isEditMode) {
      submitData.eventId = eventId;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result && !result.didSucceed()) {
      setSubmitError(result);

      return;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as VolunteerSlot[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, eventTimezone, canEditPay, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord && !endBeforeStart,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("volunteers:slots.form.editTitle", { defaultValue: "Edit volunteer slot" })
    : t("volunteers:slots.form.addTitle", { defaultValue: "Add volunteer slot" });

  // ⚠ EVERY key listed in KNOWN_FORM_FIELDS needs somewhere to land. `getFormErrors` puts a KNOWN name in
  // `fields` and NEVER in `genericErrors`, so a listed field with no control SWALLOWS its message entirely —
  // strictly worse than not listing it, because the save just fails with nothing on screen (found 2026-09-19).
  // The coordinate/radius keys have no control of their own here, so they surface on the location block.
  const locationError =
    fieldErrors.locationVenueId ||
    fieldErrors.pointOfInterestId ||
    fieldErrors.locationKind ||
    fieldErrors.streetAddress ||
    fieldErrors.latitude ||
    fieldErrors.longitude ||
    fieldErrors.radius ||
    fieldErrors.radiusUnit;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="lg"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean) || endBeforeStart}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord || endBeforeStart}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <VolunteerSlotEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <TextInput
            label={t("volunteers:slots.form.fields.name.label", { defaultValue: "Slot name" })}
            description={t("volunteers:slots.form.fields.name.description", {
              defaultValue: "A label for this specific need, e.g. 'North Gate Check-in'.",
            })}
            placeholder={t("volunteers:slots.form.fields.name.placeholder", { defaultValue: "e.g. North Gate Check-in" })}
            value={formData.name}
            onChange={(e) => setField("name", e.currentTarget.value)}
            error={fieldErrors.name}
            data-autofocus
            size="md"
          />

          <VolunteerRoleSelect
            label={t("volunteers:slots.form.fields.role.label", { defaultValue: "Role" })}
            description={t("volunteers:slots.form.fields.role.description", {
              defaultValue: "Reuse a role from your library, or leave blank for an ad-hoc slot.",
            })}
            value={formData.volunteerRoleId}
            onChange={(id) => setField("volunteerRoleId", id)}
            onRoleResolved={setPickedRole}
            error={fieldErrors.volunteerRoleId}
          />

          <NumberInput
            label={t("volunteers:slots.form.fields.quantity.label", { defaultValue: "How many needed" })}
            value={formData.quantityNeeded}
            onChange={(v) => setField("quantityNeeded", Math.max(1, Number(v) || 1))}
            min={1}
            max={999}
            allowDecimal={false}
            allowNegative={false}
            error={fieldErrors.quantityNeeded}
            size="md"
          />

          <Divider label={t("volunteers:slots.form.sections.when", { defaultValue: "When" })} labelPosition="left" />

          <Group grow align="flex-start">
            <DateTimeField
              name="startsAt"
              label={t("volunteers:slots.form.fields.startsAt.label", { defaultValue: "Starts" })}
              description={t("volunteers:slots.form.fields.tzHint", { defaultValue: "Event time ({{tz}})", tz: tzAbbrev })}
              placeholder={t("volunteers:slots.form.fields.timePh", { defaultValue: "Pick a date & time" })}
              value={formData.startsAt}
              onChange={(_n, v) => setStart(v)}
              error={fieldErrors.startsAt}
              clearable
              size="md"
            />
            <DateTimeField
              name="endsAt"
              label={t("volunteers:slots.form.fields.endsAt.label", { defaultValue: "Ends" })}
              description={t("volunteers:slots.form.fields.tzHint", { defaultValue: "Event time ({{tz}})", tz: tzAbbrev })}
              placeholder={t("volunteers:slots.form.fields.timePh", { defaultValue: "Pick a date & time" })}
              value={formData.endsAt}
              onChange={(_n, v) => setField("endsAt", v)}
              notBefore={formData.startsAt}
              error={fieldErrors.endsAt || (endBeforeStart ? t("volunteers:slots.form.fields.endsAt.beforeStart", { defaultValue: "Must be after the start time." }) : undefined)}
              clearable
              size="md"
            />
          </Group>

          <Divider label={t("volunteers:slots.form.sections.where", { defaultValue: "Where" })} labelPosition="left" />

          <VolunteerLocationFields
            value={formData.location}
            onChange={(loc) => setField("location", loc)}
            eventId={eventId}
            fallbackVenueLabel={record?.locationLabel}
            error={locationError}
          />

          <Divider label={t("volunteers:slots.form.sections.details", { defaultValue: "Details" })} labelPosition="left" />

          <Textarea
            label={t("volunteers:slots.form.fields.instructions.label", { defaultValue: "Instructions" })}
            description={t("volunteers:slots.form.fields.instructions.description", {
              defaultValue: "Nuances or responsibilities specific to this slot. Markdown supported.",
            })}
            placeholder={t("volunteers:slots.form.fields.instructions.placeholder", {
              defaultValue: "e.g. Bring a hi-vis vest; report to the coordinator at 8:45am",
            })}
            value={formData.instructions}
            onChange={(e) => setField("instructions", e.currentTarget.value)}
            error={fieldErrors.instructions}
            autosize
            minRows={2}
            maxRows={6}
            size="md"
          />

          <VolunteerRewardEditor
            value={formData.rewards}
            onChange={(rewards) => setField("rewards", rewards)}
            eventId={eventId}
            error={fieldErrors.rewards}
          />

          {canEditPay ? (
            <>
              <Divider label={t("volunteers:slots.form.sections.pay", { defaultValue: "Pay" })} labelPosition="left" />

              <MoneyInput
                label={t("volunteers:slots.form.fields.hourlyRate.label", { defaultValue: "Hourly rate" })}
                description={
                  pickedRole?.hourlyRate != null
                    ? t("volunteers:slots.form.fields.hourlyRate.descriptionRole", {
                        defaultValue: "Leave empty to pay the role's rate ({{rate}} / h). Set it to pay this shift differently.",
                        rate: formatCurrency(pickedRole.hourlyRate, currencyCode || undefined),
                      })
                    : t("volunteers:slots.form.fields.hourlyRate.description", {
                        defaultValue: "Leave empty for unpaid volunteers. Set it to pay whoever works this shift.",
                      })
                }
                placeholder={
                  pickedRole?.hourlyRate != null
                    ? t("volunteers:slots.form.fields.hourlyRate.placeholderRole", { defaultValue: "The role's rate" })
                    : t("volunteers:slots.form.fields.hourlyRate.placeholder", { defaultValue: "Unpaid" })
                }
                value={formData.hourlyRate}
                onChange={(v) => setField("hourlyRate", v)}
                currency={currencyCode}
                error={fieldErrors.hourlyRate}
              />
            </>
          ) : null}

          <Divider label={t("volunteers:slots.form.sections.signUps", { defaultValue: "Sign-ups on the event website" })} labelPosition="left" />

          {/* ⚠ Starts EMPTY on add, like the sponsorship offering's: the server refuses a create without it, and
              the refusal lands here — better than a switch that quietly defaulted to open. */}
          <Radio.Group
            label={t("volunteers:slots.form.fields.signUps.label", { defaultValue: "Public sign-ups" })}
            description={t("volunteers:slots.form.fields.signUps.description", {
              defaultValue: "Whether people can sign up for this shift from the volunteer block on your event page.",
            })}
            value={formData.isOpenForApplications === null ? "" : formData.isOpenForApplications ? "open" : "closed"}
            onChange={(v) => setField("isOpenForApplications", v === "open")}
            error={fieldErrors.isOpenForApplications}
            size="md"
          >
            <Stack gap="xs" mt="xs">
              <Radio value="open" label={t("volunteers:slots.form.fields.signUps.open", { defaultValue: "Open — people can sign up" })} size="md" />
              <Radio value="closed" label={t("volunteers:slots.form.fields.signUps.closed", { defaultValue: "Closed — staffed by you only" })} size="md" />
            </Stack>
          </Radio.Group>

          {/* ⚠ Each option says what it does to a stranger's sign-up. */}
          <Radio.Group
            label={t("volunteers:slots.form.fields.approvalMode.label", { defaultValue: "When somebody signs up" })}
            value={formData.approvalMode}
            onChange={(v) => setField("approvalMode", v)}
            error={fieldErrors.approvalMode}
            size="md"
          >
            <Stack gap="xs" mt="xs">
              <Radio
                value={VOLUNTEER_APPROVAL_MODE.REVIEW}
                label={t("volunteers:slots.form.fields.approvalMode.review", { defaultValue: "I review it — the place is taken when I approve" })}
                size="md"
              />
              <Radio
                value={VOLUNTEER_APPROVAL_MODE.AUTO}
                label={t("volunteers:slots.form.fields.approvalMode.auto", { defaultValue: "Confirm automatically while places last" })}
                size="md"
              />
            </Stack>
          </Radio.Group>

          {formData.isOpenForApplications ? (
            <DateTimeField
              name="applicationsCloseAt"
              label={t("volunteers:slots.form.fields.applicationsCloseAt.label", { defaultValue: "Sign-ups close" })}
              description={t("volunteers:slots.form.fields.applicationsCloseAt.description", {
                defaultValue: "Optional. Leave empty to take sign-ups until the shift starts. Event time ({{tz}}).",
                tz: tzAbbrev,
              })}
              placeholder={t("volunteers:slots.form.fields.timePh", { defaultValue: "Pick a date & time" })}
              value={formData.applicationsCloseAt}
              onChange={(_n, v) => setField("applicationsCloseAt", v)}
              error={fieldErrors.applicationsCloseAt}
              clearable
              size="md"
            />
          ) : null}

          <StatusInput value={formData.status} onChange={(status) => setField("status", status)} error={fieldErrors.status} />
        </Stack>
      )}
    </AppDrawer>
  );
};
